import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Button, TextField,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Box, LinearProgress, Pagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export default function PieDetailsDialog({
  open,
  onClose,
  title = 'All Items',
  dialogTotal = 0,
  query,
  setQuery,
  rows = [],
  entityName = 'Item',
  rowsPerPage = 10, // default rows per page
}) {
  const [page, setPage] = useState(1);

  const filtered = rows.filter((r) =>
    (query || '').trim().toLowerCase() === '' ? true : r.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  const entityLower = String(entityName || 'Item').toLowerCase();
  const totalCost = filtered.reduce((sum, row) => {
    const cost = Number(row.issue_cost ?? row.icd_cost ?? row.cpt_cost ?? row.drug_cost ?? 0);
    return sum + cost;
  }, 0);

  const pageCount = Math.ceil(filtered.length / rowsPerPage);
  const displayedRows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  
  useEffect(() => {
    setPage(1)
     // eslint-disable-next-line
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, minHeight: '60vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">Total Medicals: {filtered.length}</Typography>
        <Typography variant="body2" color="text.secondary">Total Counts: {dialogTotal.toLocaleString()}</Typography>
        <Typography variant="body2" color="text.secondary">
          Total Cost: ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={`Search ${entityLower}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480, borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 400, width: 56 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{entityName}</TableCell>
                <TableCell sx={{ fontWeight: 500 }} align="center">Cost</TableCell>
                <TableCell sx={{ fontWeight: 500 }} align="center">Count</TableCell>
                <TableCell sx={{ fontWeight: 500, width: 180 }} align="center">Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((row, idx) => {
                const percent = dialogTotal ? (row.value / dialogTotal) * 100 : 0;
                return (
                  <TableRow
                    key={`${row.name}-${idx}`}
                    sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="center">
                      $
                      {(
                        Number(
                          row.issue_cost ??
                          row.icd_cost ??
                          row.cpt_cost ??
                          row.drug_cost
                        )
                      ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "-"}
                    </TableCell>
                    <TableCell align="center">{row.value.toLocaleString()}</TableCell>
                    <TableCell align="center" sx={{ minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, percent)}
                            sx={{ height: 6, borderRadius: 999 }}
                          />
                        </Box>
                        <Box sx={{ width: 42 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            <span style={{ fontFamily: 'Outfit' }}>{percent.toFixed(2)}</span>%
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" disableElevation>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
